import React, { useEffect, useState } from "react";
import api from "../api/api";
import { useTranslation } from "react-i18next";
import "./AdminManagement.css";
import "./FaqManagement.css";

export default function FaqReact() {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, active, inactive
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, alphabetical
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    if (showForm && (newQuestion || newAnswer)) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  }, [newQuestion, newAnswer, showForm]);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 4000);
  }; 

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/faqs");
      setFaqs(res.data.data || res.data);
    } catch (err) {
      console.error(err);
      setFaqs([]);
      showNotification(t("failedLoadFaqs"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newQuestion.trim() || !newAnswer.trim()) {
      showNotification(t("fillBoth"), "warning");
      return;
    }

    setSubmitting(true);
    try {
      if (editId) {
        const res = await api.put(`/admin/edit-faq/${editId}`, {
          question: newQuestion.trim(),
          answer: newAnswer.trim(),
        });
        setFaqs((prev) => prev.map(f => f.id === editId ? { ...f, question: newQuestion.trim(), answer: newAnswer.trim() } : f));
        showNotification(t("faqUpdatedSuccess"));
              fetchFaqs()

      } else {
        const res = await api.post("/admin/add-faq", {
          question: newQuestion.trim(),
          answer: newAnswer.trim(),
          is_active: true,
        });
        // Add new FAQ at the beginning of the array
        setFaqs((prev) => [res.data, ...prev]);
        showNotification(t("faqAddedSuccess"));
              fetchFaqs()

      }
      resetForm();
    } catch (err) {
      console.error(err);
      showNotification(t("couldNotSave"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const faq = faqs.find(f => f.id === id);
    
    setActionLoading(prev => ({ ...prev, [`delete-${id}`]: true }));
    try {
      await api.delete(`/admin/delete-faq/${id}`);
      setFaqs((prev) => prev.filter(f => f.id !== id));
      showNotification(t("faqDeletedSuccess"));
      fetchFaqs()
    } catch (err) {
      console.error(err);
      showNotification(t("couldNotDelete"), "error");
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete-${id}`]: false }));
    }
  };

  const toggleActive = async (faq) => {
    setActionLoading(prev => ({ ...prev, [`toggle-${faq.id}`]: true }));
    try {
      const res = await api.put(`/admin/edit-faq/${faq.id}`, { is_active: !faq.is_active });
      setFaqs((prev) => prev.map(f => f.id === faq.id ? { ...f, is_active: !faq.is_active } : f));
      showNotification(t("faqStatusChanged", { status: !faq.is_active ? t("activated") : t("deactivated") }));
    } catch (err) {
      console.error(err);
      showNotification(t("couldNotUpdateStatus"), "error");
    } finally {
      setActionLoading(prev => ({ ...prev, [`toggle-${faq.id}`]: false }));
    }
  };

  const startEdit = (faq) => {
    setEditId(faq.id);
    setNewQuestion(faq.question);
    setNewAnswer(faq.answer);
    setShowForm(true);
    // Scroll to form smoothly
    setTimeout(() => {
      document.querySelector('.form-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditId(null);
    setNewQuestion("");
    setNewAnswer("");
    setUnsavedChanges(false);
  };

  const quickAdd = () => {
    setEditId(null);
    setNewQuestion("");
    setNewAnswer("");
    setShowForm(true);
    setTimeout(() => {
      document.querySelector('#questionInput')?.focus();
    }, 100);
  };

  // Filter and search logic
  const filteredAndSortedFaqs = faqs
    .filter(faq => {
      if (!faq || !faq.question || !faq.answer) return false;
      
      const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filter === "all" || 
                           (filter === "active" && faq.is_active) ||
                           (filter === "inactive" && !faq.is_active);
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return (a.id || 0) - (b.id || 0);
        case "alphabetical":
          return (a.question || "").localeCompare(b.question || "");
        case "newest":
        default:
          return (b.id || 0) - (a.id || 0);
      }
    });

  const activeCount = faqs.filter(f => f.is_active).length;
  const inactiveCount = faqs.filter(f => !f.is_active).length;

  return (
    <>
      {/* Notification */}
      <div className={`notification alert ${notification.show ? 'show' : ''} ${notification.type}`}>
        <div className="d-flex align-items-center">
          <div className="me-2">
            {notification.type === 'success' && <i className="fas fa-check" aria-hidden="true" />}
            {notification.type === 'error' && <i className="fas fa-xmark" aria-hidden="true" />}
            {notification.type === 'warning' && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
          </div>
          {notification.message}
        </div>
      </div>

      <div className="container-fluid admin-management-page faq-management-page" style={{minHeight: '100vh'}}>
        <div className="container faq-shell">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <div className="py-5">
                {/* Header with Stats */}
                <div className="mb-5">
                  <div className="d-flex justify-content-between align-items-center mb-4 faq-header-row">
                    <div>
                      <h1 className="display-5 main-title mb-2">
                        {t("faqManagement")}
                        {unsavedChanges && <span className="unsaved-indicator ms-2"></span>}
                      </h1>
                    </div>
                    <button
                      className={`btn btn-lg ${showForm ? 'btn-outline-custom' : 'btn-primary-custom'}`}
                      onClick={showForm ? resetForm : quickAdd}
                    >
                      {showForm ? (
                        <><i className="fas fa-xmark me-1" aria-hidden="true" />{t("cancel")}</>
                      ) : (
                        <><i className="fas fa-plus me-1" aria-hidden="true" />{t("addFaq")}</>
                      )}
                    </button>
                  </div>
                  
                  {/* Stats Cards */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-4">
                      <div className="stats-card faq-stat-card p-3 text-center">
                        <h3 className="h4 mb-1" style={{color: 'var(--sf-primary-turquoise)'}}>{faqs.length}</h3>
                        <small className="text-muted">{t("totalFaqs")}</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="stats-card faq-stat-card p-3 text-center">
                        <h3 className="h4 mb-1 text-success">{activeCount}</h3>
                        <small className="text-muted">{t("active")}</small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="stats-card faq-stat-card p-3 text-center">
                        <h3 className="h4 mb-1 text-danger">{inactiveCount}</h3>
                        <small className="text-muted">{t("inactive")}</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search and Filter Controls */}
                {faqs.length > 0 && (
                  <div className="search-controls faq-search-controls p-4 mb-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control form-control-lg ps-5"
                            placeholder={t("searchFaqs")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                            <i className="fas fa-magnifying-glass" aria-hidden="true" />
                          </span>
                          {searchTerm && (
                            <button
                              className="position-absolute top-50 end-0 translate-middle-y me-3 btn btn-sm"
                              onClick={() => setSearchTerm("")}
                              aria-label={t("reset")}
                              title={t("reset")}
                            >
                              <i className="fas fa-xmark" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-md-3">
                        <select 
                          className="form-select form-select-lg" 
                          value={filter} 
                          onChange={(e) => setFilter(e.target.value)}
                        >
                          <option value="all">{t("all")} ({faqs.length})</option>
                          <option value="active">{t("active")} ({activeCount})</option>
                          <option value="inactive">{t("inactive")} ({inactiveCount})</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <select 
                          className="form-select form-select-lg" 
                          value={sortBy} 
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          <option value="newest">{t("newestFirst")}</option>
                          <option value="oldest">{t("oldestFirst")}</option>
                          <option value="alphabetical">{t("az")}</option>
                        </select>
                      </div>
                    </div>
                    {searchTerm && (
                      <div className="mt-2">
                        <small className="text-muted">
                          {t("foundResults", { count: filteredAndSortedFaqs.length, term: searchTerm })}
                        </small>
                      </div>
                    )}
                  </div>
                )}

                {/* Form */}
                {showForm && (
                  <div className="card form-card mb-5 fade-in">
                    <div className="card-body p-5">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="card-title main-title mb-0">
                          {editId ? (
                            <><i className="fas fa-pen me-2" aria-hidden="true" />{t("editFaq")}</>
                          ) : (
                            <><i className="fas fa-plus me-2" aria-hidden="true" />{t("addNewFaq")}</>
                          )}
                        </h4>
                        {unsavedChanges && (
                          <span className="badge bg-warning text-dark">
                            {t("unsavedChanges")}
                          </span>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <label className="form-label fw-bold text-dark">{t("question")} *</label>
                        <input
                          id="questionInput"
                          type="text"
                          className="form-control form-control-lg"
                          value={newQuestion}
                          onChange={(e) => setNewQuestion(e.target.value)}
                          placeholder={t("question")}
                          maxLength={200}
                        />
                        <div className={`character-count ${newQuestion.length > 180 ? 'warning' : newQuestion.length > 195 ? 'danger' : ''}`}>
                          {t("characters", { count: newQuestion.length, max: 200 })}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="form-label fw-bold text-dark">{t("answer")} *</label>
                        <textarea
                          rows={5}
                          className="form-control form-control-lg"
                          value={newAnswer}
                          onChange={(e) => setNewAnswer(e.target.value)}
                          placeholder={t("answer")}
                          maxLength={1000}
                          style={{resize: 'vertical'}}
                        />
                        <div className={`character-count ${newAnswer.length > 900 ? 'warning' : newAnswer.length > 980 ? 'danger' : ''}`}>
                          {t("characters", { count: newAnswer.length, max: 1000 })}
                        </div>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-3">
                          <button
                            className="btn btn-primary-custom btn-lg"
                            onClick={handleSubmit}
                            disabled={submitting || !newQuestion.trim() || !newAnswer.trim()}
                          >
                            {submitting ? (
                              <>
                                <span className="mini-spinner me-2"></span>
                                {editId ? `${t("updating")}...` : `${t("saving")}...`}
                              </>
                            ) : (
                              <>
                                <i className="fas fa-floppy-disk me-1" aria-hidden="true" />
                                {editId ? t("updateFaq") : t("saveFaq")}
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-outline-custom btn-lg"
                            onClick={resetForm}
                            disabled={submitting}
                          >
                            {t("cancel")}
                          </button>
                        </div>
                        
                        {editId && (
                          <button
                            className="btn btn-danger-light btn-sm"
                            onClick={() => handleDelete(editId)}
                            disabled={submitting}
                          >
                            <i className="fas fa-trash me-1" aria-hidden="true" />
                            {t("delete")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Content */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="loading-spinner mx-auto mb-4"></div>
                    <h5 className="text-muted mb-2">{t("loadingFaqs")}</h5>
                    <p className="text-muted small">{t("pleaseWait")}</p>
                  </div>
                ) : filteredAndSortedFaqs.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="empty-state-icon">
                      <svg width="40" height="40" fill="currentColor" className="text-white" viewBox="0 0 24 24">
                        <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </div>
                    <h4 className="main-title mb-3">
                      {searchTerm ? t("noFaqsFound") : t("noFaqsYet")}
                    </h4>
                    <p className="text-muted mb-4">
                      {searchTerm 
                        ? t("noMatchSearch", { term: searchTerm })
                        : t("getStarted")
                      }
                    </p>
                    {!showForm && (
                      <button className="btn btn-primary-custom btn-lg" onClick={quickAdd}>
                        <i className="fas fa-plus me-1" aria-hidden="true" />
                        {t("createFirstFaq")}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="row g-4">
                    {filteredAndSortedFaqs.map((faq, index) => (
                      <div key={faq.id} className="col-12 fade-in" style={{animationDelay: `${index * 0.1}s`}}>
                        <div className={`card card-custom ${!faq.is_active ? 'faq-item-inactive' : ''}`}>
                          <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start faq-card-layout">
                              <div className="flex-grow-1 me-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <div className="d-flex align-items-center gap-3">
                                    <span className={`badge ${faq.is_active ? 'badge-active' : 'badge-inactive'} px-3 py-2`}>
                                      ● {faq.is_active ? t("active") : t("inactive")}
                                    </span>
                                  </div>
                                </div>
                                
                                <h5 className="card-title main-title mb-3">
                                  {faq.question}
                                </h5>
                                
                                <div className="faq-answer">
                                  <p className="card-text text-muted lh-base mb-0">
                                    {faq.answer}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="d-flex flex-column gap-2 faq-card-actions" style={{minWidth: '120px'}}>
                                <button
                                  className={`btn btn-sm-custom ${
                                    faq.is_active ? 'btn-success-light' : 'btn-danger-light'
                                  }`}
                                  onClick={() => toggleActive(faq)}
                                  disabled={actionLoading[`toggle-${faq.id}`]}
                                  title={faq.is_active ? t('click_to_deactivate') : t('click_to_activate')}
                                >
                                  {actionLoading[`toggle-${faq.id}`] ? (
                                    <span className="mini-spinner"></span>
                                  ) : (
                                    <>
                                      <i className={`fas ${faq.is_active ? 'fa-circle-xmark' : 'fa-circle-check'} me-1`} aria-hidden="true" />
                                      {faq.is_active ? t("deactivate") : t("activate")}
                                    </>
                                  )}
                                </button>

                                <button
                                  className="btn btn-sm-custom btn-turquoise-light"
                                  onClick={() => startEdit(faq)}
                                  disabled={submitting || actionLoading[`toggle-${faq.id}`]}
                                  title={t('edit_this_faq')}
                                >
                                  <i className="fas fa-pen me-1" aria-hidden="true" />
                                  {t("edit")}
                                </button>

                                <button
                                  className="btn btn-sm-custom btn-danger-light"
                                  onClick={() => handleDelete(faq.id)}
                                  disabled={actionLoading[`delete-${faq.id}`] || submitting}
                                  title={t('delete_this_faq_permanently')}
                                >
                                  {actionLoading[`delete-${faq.id}`] ? (
                                    <span className="mini-spinner"></span>
                                  ) : (
                                    <><i className="fas fa-trash me-1" aria-hidden="true" />{t("delete")}</>
                                  )}
                                </button>
                              </div>
                            </div>
                            
                            {/* Action Feedback */}
                            {(actionLoading[`toggle-${faq.id}`] || actionLoading[`delete-${faq.id}`]) && (
                              <div className="mt-3 p-2 bg-light rounded small text-muted text-center">
                                <span className="mini-spinner me-2"></span>
                                {t("processing")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Bulk Actions */}
                {filteredAndSortedFaqs.length > 1 && (
                  <div className="mt-5 p-4 bg-light rounded-4">
                    <h6 className="mb-3">
                      <i className="fas fa-screwdriver-wrench me-2" aria-hidden="true" />
                      {t("bulkActions")}
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-sm btn-success-light"
                        onClick={() => {
                          filteredAndSortedFaqs.forEach(faq => {
                            if (!faq.is_active) toggleActive(faq);
                          });
                        }}
                      >
                        <i className="fas fa-circle-check me-1" aria-hidden="true" />
                        {t("activateAll")}
                      </button>
                      <button
                        className="btn btn-sm btn-danger-light"
                        onClick={() => {
                          filteredAndSortedFaqs.forEach(faq => {
                            if (faq.is_active) toggleActive(faq);
                          });
                        }}
                      >
                        <i className="fas fa-circle-xmark me-1" aria-hidden="true" />
                        {t("deactivateAll")}
                      </button>
                      <button
                        className="btn btn-sm btn-primary-custom"
                        onClick={() => {
                          const data = filteredAndSortedFaqs.map(faq => ({
                            question: faq.question,
                            answer: faq.answer,
                            status: faq.is_active ? t("active") : t("inactive")
                          }));
                          const csv = `${t("question")},${t("answer")},${t("status")}\n` +
                            data.map(row => `"${row.question}","${row.answer}","${row.status}"`).join('\n');
                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'faqs-export.csv';
                          a.click();
                          showNotification(t("faqsExported"));
                        }}
                      >
                        <i className="fas fa-download me-1" aria-hidden="true" />
                        {t("exportCsv")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
